import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { scheduleApi } from "@api";
import { RootState } from ".";
import { ReservationUpdateMinimalParamsType, ScheduleMessageType, ScheduleNewReservationParamsType, Reservation, ReservationType, ScheduleRoomType } from "src/types";

// Define API response structure
interface ApiResponse<T> {
  success: boolean;
  data: T;
  message: string;
  status: number;
}

// Define new room input data
interface NewRoomData {
  name: string;
  capacity: number;
}

// Define new participant input data
interface NewParticipantData {
  reservation_id: number;
  user_id: number;
  required: boolean;
}

// Define update participant input data
interface UpdateParticipantData {
  id: number;
  required?: boolean;
}

// Define new message input data
interface NewMessageData {
  reservation_id: number | null;
  temp_reservation_id?: number;
  content?: string;
  file_url?: string;
}

// Define update message input data
interface UpdateMessageData {
  id: number;
  content?: string;
  file_url?: string;
}

// Define new list input data
interface NewListData {
    type: 'favorite' | 'default';
    participants: number[];
    name?: string;
}

interface UpdateListData {
    id: number;
    type?: 'favorite' | 'default';
    participants?: number[];
    name?: string;
}

// Define new file url request data
interface RequestFileUrlData {
  file_name: string;
}

// Define state interface
interface ScheduleState {
  rooms: {
    data: ScheduleRoomType[];
    loading: boolean;
    error: boolean;
  };
  types: {
    data: ReservationType[];
    loading: boolean;
    error: boolean;
  };
  reservations: {
    data: Reservation[];
    loading: boolean;
    error: boolean;
    filters: {
      room_ids?: number[];
      start_date?: string;
      end_date?: string;
    };
  };
  messages: {
    data: ScheduleMessageType[];
    loading: boolean;
    error: boolean;
    currentReservationId: number | null;
  };
  fileUrl: {
    data: string | null;
    loading: boolean;
    error: boolean;
  };
  lists: {
    data: {
      id: number;
      type: 'favorite' | 'default';
      participants: number[];
      name?: string;
      creator_id: number;
      deleted_at: string | null;
    }[];
    loading: boolean;
    error: boolean;
  };
  operations: {
    loading: boolean;
    error: string | null;
    success: boolean;
  };
}

// Initial state
const initialState: ScheduleState = {
  rooms: {
    data: [],
    loading: false,
    error: false
  },
  types: {
    data: [],
    loading: false,
    error: false
  },
  reservations: {
    data: [],
    loading: false,
    error: false,
    filters: {}
  },
  messages: {
    data: [],
    loading: false,
    error: false,
    currentReservationId: null
  },
  fileUrl: {
    data: null,
    loading: false,
    error: false
  },
  lists: {
    data: [],
    loading: false,
    error: false
  },
  operations: {
    loading: false,
    error: null,
    success: false
  }
};

// API services are imported from @api/schedule

// Async Thunks

// Room thunks
export const fetchRooms = createAsyncThunk(
  "schedule/fetchRooms",
  async (_, { rejectWithValue }) => {
    try {
      const response = await scheduleApi.getRooms();
      return response.data; // API returns { success, data, message, status }
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to fetch rooms");
    }
  }
);

export const createRoom = createAsyncThunk(
  "schedule/createRoom",
  async (roomData: NewRoomData, { rejectWithValue, dispatch }) => {
    try {
      const response = await scheduleApi.createRoom(roomData);
      // Refresh the rooms list after creating a new room
      dispatch(fetchRooms());
      return response.data; // API returns { success, data, message, status }
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to create room");
    }
  }
);

// Type thunks
export const fetchTypes = createAsyncThunk(
  "schedule/fetchTypes",
  async (_, { rejectWithValue }) => {
    try {
      const response = await scheduleApi.getTypes();
      return response.data; // API returns { success, data, message, status }
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to fetch types");
    }
  }
);

// Reservation thunks
export const fetchReservations = createAsyncThunk(
  "schedule/fetchReservations",
  async (filters: { room_ids?: number[]; start_date?: string; end_date?: string; } | undefined, { rejectWithValue }) => {
    try {
      const response = await scheduleApi.getReservations(filters);
      return { data: response.data, filters }; // API returns { success, data, message, status }
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to fetch reservations");
    }
  }
);

export const createReservation = createAsyncThunk(
  "schedule/createReservation",
  async (reservationData: ScheduleNewReservationParamsType, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await scheduleApi.createReservation(reservationData);
      
      // Refresh reservations with current filters
      const state = getState() as RootState;
      const currentFilters = state.schedule.reservations.filters;
      dispatch(fetchReservations(currentFilters));
      
      return response.data; // API returns { success, data, message, status }
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to create reservation");
    }
  }
);

export const updateReservation = createAsyncThunk(
  "schedule/updateReservation",
  async (reservationData: ReservationUpdateMinimalParamsType, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await scheduleApi.updateReservation(reservationData);
      // Refresh reservations with current filters
      const state = getState() as RootState;
      const currentFilters = state.schedule.reservations.filters;
      dispatch(fetchReservations(currentFilters));
      return response.data; // API returns { success, data, message, status }
    } catch (err: any) {
      // Make sure to return the full error structure
      if (err.response?.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue("Failed to update reservation");
    }
  }
);

export const deleteReservation = createAsyncThunk(
  "schedule/deleteReservation",
  async (id: number, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await scheduleApi.deleteReservation(id);
      
      // Refresh reservations with current filters
      const state = getState() as RootState;
      const currentFilters = state.schedule.reservations.filters;
      dispatch(fetchReservations(currentFilters));
      
      return response.data; // API returns { success, data, message, status }
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to delete reservation");
    }
  }
);

export const cancelReservation = createAsyncThunk(
  "schedule/cancelReservation",
  async (id: number, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await scheduleApi.cancelReservation(id);
      
      // Refresh reservations with current filters
      const state = getState() as RootState;
      const currentFilters = state.schedule.reservations.filters;
      dispatch(fetchReservations(currentFilters));
      
      return response.data; // API returns { success, data, message, status }
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to cancel reservation");
    }
  }
);

// Participant thunks
export const addParticipant = createAsyncThunk(
  "schedule/addParticipant",
  async (participantData: NewParticipantData, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await scheduleApi.addParticipant(participantData);
      
      // Refresh reservations with current filters
      const state = getState() as RootState;
      const currentFilters = state.schedule.reservations.filters;
      dispatch(fetchReservations(currentFilters));
      
      return response.data; // API returns { success, data, message, status }
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to add participant");
    }
  }
);

export const updateParticipant = createAsyncThunk(
  "schedule/updateParticipant",
  async (participantData: UpdateParticipantData, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await scheduleApi.updateParticipant(participantData);
      
      // Refresh reservations with current filters
      const state = getState() as RootState;
      const currentFilters = state.schedule.reservations.filters;
      dispatch(fetchReservations(currentFilters));
      
      return response.data; // API returns { success, data, message, status }
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to update participant");
    }
  }
);

export const deleteParticipant = createAsyncThunk(
  "schedule/deleteParticipant",
  async (id: number, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await scheduleApi.deleteParticipant(id);
      
      // Refresh reservations with current filters
      const state = getState() as RootState;
      const currentFilters = state.schedule.reservations.filters;
      dispatch(fetchReservations(currentFilters));
      
      return response.data; // API returns { success, data, message, status }
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to delete participant");
    }
  }
);

// Message thunks
export const fetchMessages = createAsyncThunk(
  "schedule/fetchMessages",
  async (filters: { creator_id?: number; reservation_id?: number; } | undefined, { rejectWithValue }) => {
    try {
      const response = await scheduleApi.getMessages(filters);
      return { data: response.data, filters }; // API returns { success, data, message, status }
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to fetch messages");
    }
  }
);

export const fetchReservationMessages = createAsyncThunk(
  "schedule/fetchReservationMessages",
  async (reservationId: number, { rejectWithValue }) => {
    try {
      const response = await scheduleApi.getReservationMessages(reservationId);
      return { data: response.data, reservationId }; // API returns { success, data, message, status }
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to fetch reservation messages");
    }
  }
);

export const createMessage = createAsyncThunk(
  "schedule/createMessage",
  async (messageData: NewMessageData, { rejectWithValue, dispatch }) => {
    try {
      const response = await scheduleApi.createMessage(messageData);
      
      // Refresh messages for this reservation
      if (messageData.reservation_id) {
        dispatch(fetchReservationMessages(messageData.reservation_id));
      } else {
        dispatch(fetchMessages());
      }
      
      return response.data; // API returns { success, data, message, status }
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to create message");
    }
  }
);

export const updateMessage = createAsyncThunk(
  "schedule/updateMessage",
  async (messageData: UpdateMessageData, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await scheduleApi.updateMessage(messageData);
      
      // Refresh messages using current reservation ID if available
      const state = getState() as RootState;
      const currentReservationId = state.schedule.messages.currentReservationId;
      
      if (currentReservationId) {
        dispatch(fetchReservationMessages(currentReservationId));
      } else {
        dispatch(fetchMessages());
      }
      
      return response.data; // API returns { success, data, message, status }
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to update message");
    }
  }
);

export const deleteMessage = createAsyncThunk(
  "schedule/deleteMessage",
  async (id: number, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await scheduleApi.deleteMessage(id);
      
      // Refresh messages using current reservation ID if available
      const state = getState() as RootState;
      const currentReservationId = state.schedule.messages.currentReservationId;
      
      if (currentReservationId) {
        dispatch(fetchReservationMessages(currentReservationId));
      } else {
        dispatch(fetchMessages());
      }
      
      return response.data; // API returns { success, data, message, status }
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to delete message");
    }
  }
);

export const requestFileUrl = createAsyncThunk(
  "schedule/requestFileUrl",
  async (params: { filename: string, file_type: string, acl: string }, { rejectWithValue }) => {
    try {
      const response = await scheduleApi.requestFileUrl(params);
      return response.data; // API returns { success, data, message, status }
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to request file URL");
    }
  }
);

// List thunks
export const fetchLists = createAsyncThunk(
  "schedule/fetchLists",
  async (_, { rejectWithValue }) => {
    try {
      //const user = useAppSelector(selectAuth);
      //const userId = user.userInfo?.id;

      const response = await scheduleApi.getLists();
      return response.data; // API returns { success, data, message, status }
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to fetch lists");
    }
  }
);

export const createList = createAsyncThunk(
  "schedule/createList",
  async (listData: NewListData, { rejectWithValue, dispatch }) => {
    try {
      const response = await scheduleApi.createList(listData);
      // Refresh lists after creating
      dispatch(fetchLists());
      return response.data; // API returns { success, data, message, status }
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to create list");
    }
  }
);

export const updateList = createAsyncThunk(
  "schedule/updateList",
  async (listData: UpdateListData, { rejectWithValue, dispatch }) => {
    try {
      const response = await scheduleApi.updateList(listData);
      // Refresh lists after updating
      dispatch(fetchLists());
      return response.data; // API returns { success, data, message, status }
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to update list");
    }
  }
);

export const deleteList = createAsyncThunk(
  "schedule/deleteList",
  async (id: number, { rejectWithValue, dispatch }) => {
    try {
      const response = await scheduleApi.deleteList(id);
      // Refresh lists after deleting
      dispatch(fetchLists());
      return response.data; // API returns { success, data, message, status }
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to delete list");
    }
  }
);

// Create the schedule slice
const scheduleSlice = createSlice({
  name: "schedule",
  initialState,
  reducers: {
    resetOperationStatus: (state) => {
      state.operations.loading = false;
      state.operations.error = null;
      state.operations.success = false;
    }
  },
  extraReducers: (builder) => {
    // Room reducers
    builder.addCase(fetchRooms.pending, (state) => {
      state.rooms.loading = true;
      state.rooms.error = false;
    });
    builder.addCase(fetchRooms.fulfilled, (state, action) => {
      state.rooms.loading = false;
      state.rooms.data = action.payload.data; // Access nested data property
    });
    builder.addCase(fetchRooms.rejected, (state) => {
      state.rooms.loading = false;
      state.rooms.error = true;
    });

    // Type reducers
    builder.addCase(fetchTypes.pending, (state) => {
      state.types.loading = true;
      state.types.error = false;
    });
    builder.addCase(fetchTypes.fulfilled, (state, action) => {
      state.types.loading = false;
      state.types.data = action.payload.data; // Access nested data property
    });
    builder.addCase(fetchTypes.rejected, (state) => {
      state.types.loading = false;
      state.types.error = true;
    });

    // Reservation reducers
    builder.addCase(fetchReservations.pending, (state) => {
      state.reservations.loading = true;
      state.reservations.error = false;
    });
    builder.addCase(fetchReservations.fulfilled, (state, action) => {
      state.reservations.loading = false;
      state.reservations.data = action.payload.data.data; // Access nested data.data property
      if (action.payload.filters) {
        state.reservations.filters = action.payload.filters;
      }
    });
    builder.addCase(fetchReservations.rejected, (state) => {
      state.reservations.loading = false;
      state.reservations.error = true;
    });

    // Operation status reducers for create, update, delete operations
    // Create room
    builder.addCase(createRoom.pending, (state) => {
      state.operations.loading = true;
      state.operations.error = null;
      state.operations.success = false;
    });
    builder.addCase(createRoom.fulfilled, (state) => {
      state.operations.loading = false;
      state.operations.success = true;
    });
    builder.addCase(createRoom.rejected, (state, action) => {
      state.operations.loading = false;
      state.operations.error = action.payload as string;
    });

    // Create reservation
    builder.addCase(createReservation.pending, (state) => {
      state.operations.loading = true;
      state.operations.error = null;
      state.operations.success = false;
    });
    builder.addCase(createReservation.fulfilled, (state) => {
      state.operations.loading = false;
      state.operations.success = true;
    });
    builder.addCase(createReservation.rejected, (state, action) => {
      state.operations.loading = false;
      state.operations.error = action.payload as string;
    });

    // Update reservation
    builder.addCase(updateReservation.pending, (state) => {
      state.operations.loading = true;
      state.operations.error = null;
      state.operations.success = false;
    });
    builder.addCase(updateReservation.fulfilled, (state) => {
      state.operations.loading = false;
      state.operations.success = true;
    });
    builder.addCase(updateReservation.rejected, (state, action) => {
      state.operations.loading = false;
      state.operations.error = action.payload as string;
    });

    // Delete reservation
    builder.addCase(deleteReservation.pending, (state) => {
      state.operations.loading = true;
      state.operations.error = null;
      state.operations.success = false;
    });
    builder.addCase(deleteReservation.fulfilled, (state) => {
      state.operations.loading = false;
      state.operations.success = true;
    });
    builder.addCase(deleteReservation.rejected, (state, action) => {
      state.operations.loading = false;
      state.operations.error = action.payload as string;
    });

    // Cancel reservation
    builder.addCase(cancelReservation.pending, (state) => {
      state.operations.loading = true;
      state.operations.error = null;
      state.operations.success = false;
    });
    builder.addCase(cancelReservation.fulfilled, (state) => {
      state.operations.loading = false;
      state.operations.success = true;
    });
    builder.addCase(cancelReservation.rejected, (state, action) => {
      state.operations.loading = false;
      state.operations.error = action.payload as string;
    });

    // Add participant
    builder.addCase(addParticipant.pending, (state) => {
      state.operations.loading = true;
      state.operations.error = null;
      state.operations.success = false;
    });
    builder.addCase(addParticipant.fulfilled, (state) => {
      state.operations.loading = false;
      state.operations.success = true;
    });
    builder.addCase(addParticipant.rejected, (state, action) => {
      state.operations.loading = false;
      state.operations.error = action.payload as string;
    });

    // Update participant
    builder.addCase(updateParticipant.pending, (state) => {
      state.operations.loading = true;
      state.operations.error = null;
      state.operations.success = false;
    });
    builder.addCase(updateParticipant.fulfilled, (state) => {
      state.operations.loading = false;
      state.operations.success = true;
    });
    builder.addCase(updateParticipant.rejected, (state, action) => {
      state.operations.loading = false;
      state.operations.error = action.payload as string;
    });

    // Delete participant
    builder.addCase(deleteParticipant.pending, (state) => {
      state.operations.loading = true;
      state.operations.error = null;
      state.operations.success = false;
    });
    builder.addCase(deleteParticipant.fulfilled, (state) => {
      state.operations.loading = false;
      state.operations.success = true;
    });
    builder.addCase(deleteParticipant.rejected, (state, action) => {
      state.operations.loading = false;
      state.operations.error = action.payload as string;
    });

    // Message reducers
    builder.addCase(fetchMessages.pending, (state) => {
      state.messages.loading = true;
      state.messages.error = false;
    });
    builder.addCase(fetchMessages.fulfilled, (state, action) => {
      state.messages.loading = false;
      state.messages.data = action.payload.data.data; // Access nested data property
    });
    builder.addCase(fetchMessages.rejected, (state) => {
      state.messages.loading = false;
      state.messages.error = true;
    });

    builder.addCase(fetchReservationMessages.pending, (state) => {
      state.messages.loading = true;
      state.messages.error = false;
    });
    builder.addCase(fetchReservationMessages.fulfilled, (state, action) => {
      state.messages.loading = false;
      state.messages.data = action.payload.data.data; // Access nested data property
      state.messages.currentReservationId = action.payload.reservationId;
    });
    builder.addCase(fetchReservationMessages.rejected, (state) => {
      state.messages.loading = false;
      state.messages.error = true;
    });

    // Create message
    builder.addCase(createMessage.pending, (state) => {
      state.operations.loading = true;
      state.operations.error = null;
      state.operations.success = false;
    });
    builder.addCase(createMessage.fulfilled, (state) => {
      state.operations.loading = false;
      state.operations.success = true;
    });
    builder.addCase(createMessage.rejected, (state, action) => {
      state.operations.loading = false;
      state.operations.error = action.payload as string;
    });

    // Update message
    builder.addCase(updateMessage.pending, (state) => {
      state.operations.loading = true;
      state.operations.error = null;
      state.operations.success = false;
    });
    builder.addCase(updateMessage.fulfilled, (state) => {
      state.operations.loading = false;
      state.operations.success = true;
    });
    builder.addCase(updateMessage.rejected, (state, action) => {
      state.operations.loading = false;
      state.operations.error = action.payload as string;
    });

    // Delete message
    builder.addCase(deleteMessage.pending, (state) => {
      state.operations.loading = true;
      state.operations.error = null;
      state.operations.success = false;
    });
    builder.addCase(deleteMessage.fulfilled, (state) => {
      state.operations.loading = false;
      state.operations.success = true;
    });
    builder.addCase(deleteMessage.rejected, (state, action) => {
      state.operations.loading = false;
      state.operations.error = action.payload as string;
    });

    // Request file URL
    builder.addCase(requestFileUrl.pending, (state) => {
      state.operations.loading = true;
      state.operations.error = null;
      state.operations.success = false;
    });
    builder.addCase(requestFileUrl.fulfilled, (state) => {
      state.operations.loading = false;
      state.operations.success = true;
    });
    builder.addCase(requestFileUrl.rejected, (state, action) => {
      state.operations.loading = false;
      state.operations.error = action.payload as string;
    });

    // Fetch lists
    builder.addCase(fetchLists.pending, (state) => {
      state.lists.loading = true;
      state.lists.error = false;
    });
    builder.addCase(fetchLists.fulfilled, (state, action) => {
      state.lists.loading = false;
      state.lists.data = action.payload.data;
    });
    builder.addCase(fetchLists.rejected, (state) => {
      state.lists.loading = false;
      state.lists.error = true;
    });

    // Create list
    builder.addCase(createList.pending, (state) => {
      state.operations.loading = true;
      state.operations.error = null;
      state.operations.success = false;
    });
    builder.addCase(createList.fulfilled, (state) => {
      state.operations.loading = false;
      state.operations.success = true;
    });
    builder.addCase(createList.rejected, (state, action) => {
      state.operations.loading = false;
      state.operations.error = action.payload as string;
    });

    // Update list
    builder.addCase(updateList.pending, (state) => {
      state.operations.loading = true;
      state.operations.error = null;
      state.operations.success = false;
    });
    builder.addCase(updateList.fulfilled, (state) => {
      state.operations.loading = false;
      state.operations.success = true;
    });
    builder.addCase(updateList.rejected, (state, action) => {
      state.operations.loading = false;
      state.operations.error = action.payload as string;
    });

    // Delete list
    builder.addCase(deleteList.pending, (state) => {
      state.operations.loading = true;
      state.operations.error = null;
      state.operations.success = false;
    });
    builder.addCase(deleteList.fulfilled, (state) => {
      state.operations.loading = false;
      state.operations.success = true;
    });
    builder.addCase(deleteList.rejected, (state, action) => {
      state.operations.loading = false;
      state.operations.error = action.payload as string;
    });
  }
});

// Export actions
export const { resetOperationStatus } = scheduleSlice.actions;

// Export selectors
export const selectRooms = (state: RootState) => state.schedule.rooms;
export const selectRoomsLoading = (state: RootState) => state.schedule.rooms.loading;
export const selectRoomsError = (state: RootState) => state.schedule.rooms.error;

export const selectTypes = (state: RootState) => state.schedule.types;
export const selectTypesLoading = (state: RootState) => state.schedule.types.loading;
export const selectTypesError = (state: RootState) => state.schedule.types.error;

export const selectReservations = (state: RootState) => state.schedule.reservations;
export const selectReservationsLoading = (state: RootState) => state.schedule.reservations.loading;
export const selectReservationsError = (state: RootState) => state.schedule.reservations.error;
export const selectReservationsFilters = (state: RootState) => state.schedule.reservations.filters;

export const selectOperationLoading = (state: RootState) => state.schedule.operations.loading;
export const selectOperationError = (state: RootState) => state.schedule.operations.error;
export const selectOperationSuccess = (state: RootState) => state.schedule.operations.success;

export const selectMessages = (state: RootState) => state.schedule.messages;
export const selectMessagesLoading = (state: RootState) => state.schedule.messages.loading;
export const selectMessagesError = (state: RootState) => state.schedule.messages.error;

export const selectFileUrl = (state: RootState) => state.schedule.fileUrl.data;
export const selectFileUrlLoading = (state: RootState) => state.schedule.fileUrl.loading;
export const selectFileUrlError = (state: RootState) => state.schedule.fileUrl.error;

export const selectLists = (state: RootState) => state.schedule.lists;
export const selectListsLoading = (state: RootState) => state.schedule.lists.loading;
export const selectListsError = (state: RootState) => state.schedule.lists.error;

export const selectCurrentReservationId = (state: RootState) => state.schedule.messages.currentReservationId;
// Export reducer
export default scheduleSlice.reducer;